import apis from '@api/client';
import { PublicCompanyResponse } from '@api/dto';
import { REDIRECT_KEY_STORAGE } from '@constants';
import { useQuery } from '@tanstack/react-query';
import React, { createContext, useCallback, useState } from 'react';

const KEY_REGISTRATION_CODE = 'registration_code';
const KEY_REGISTRATION_COMPANY_ID = 'registration_company_id';

const RegisterContext = createContext<{
  company?: PublicCompanyResponse;
  registrationCode?: string;
  isLoading?: boolean;
  hasInitialCode?: boolean;
  reset: () => void;
  updateCompanyId: (companyId?: string) => void;
  updateRegistrationCode: (code?: string) => void;
}>({
  reset: () => {},
  updateCompanyId: () => {},
  updateRegistrationCode: () => {},
});

export function useRegisterContext() {
  return React.useContext(RegisterContext);
}

export const RegisterProvider: React.FC<React.PropsWithChildren<{ initialCode?: string }>> = ({ children, initialCode }) => {
  const initialCodeValue = initialCode ?? sessionStorage.getItem(KEY_REGISTRATION_CODE) ?? undefined;
  const storedCompanyId = sessionStorage.getItem(KEY_REGISTRATION_COMPANY_ID) ?? undefined;

  const [hasInitialCode, setHasInitialCode] = useState(!!initialCode);
  const [registrationCode, setRegistrationCode] = useState(initialCodeValue);
  const [companyId, setCompanyId] = useState(storedCompanyId);

  const { data: company, isLoading } = useQuery({
    queryKey: ['publicCompany', companyId],
    queryFn: () => apis.public.getPublicCompany({ uuid: companyId! }).then(res => res.data),
    enabled: !!companyId,
  });

  const updateRegistrationCode = useCallback((code?: string) => {
    setRegistrationCode(code);
    if (code) {
      sessionStorage.setItem(KEY_REGISTRATION_CODE, code);
    } else {
      sessionStorage.removeItem(KEY_REGISTRATION_CODE);
    }
  }, []);

  const updateCompanyId = useCallback((companyId?: string) => {
    setCompanyId(companyId);
    if (companyId) {
      sessionStorage.setItem(KEY_REGISTRATION_COMPANY_ID, companyId);
    } else {
      sessionStorage.removeItem(KEY_REGISTRATION_COMPANY_ID);
    }
  }, []);

  const reset = () => {
    sessionStorage.removeItem(REDIRECT_KEY_STORAGE);
    updateCompanyId(undefined);
    updateRegistrationCode(undefined);
    setHasInitialCode(false);
  };

  return (
    <RegisterContext.Provider
      value={{
        company,
        registrationCode,
        isLoading,
        hasInitialCode,
        reset,
        updateCompanyId,
        updateRegistrationCode,
      }}
    >
      {children}
    </RegisterContext.Provider>
  );
};
